import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { ENV } from '@environment';
import { RootState } from 'store';

import useStoreTranslation from 'hooks/useStoreTranslation';
import { Airport } from 'models/airport';
import { FlightGroupedResultResponse } from 'models/flightSearch';
import { UpSellOption } from 'models/upsell';
import UpSellOptions from 'screens/components/UpsellOptions';
import { amplitudelogEvent, ViewUpsellOptionsEvent } from 'services/amplitudeService';
import { getUpSellFares, UpSellRequest } from 'services/upsellService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount } from 'utils/generic';

interface Props {
  show: boolean;
  onClose: () => void;
  onSelect: (optionId: number, upSellId: string) => void;
  onChange: (optionId: number) => void;
  selectedFlight?: Pick<FlightGroupedResultResponse, 'provider' | 'optionId' | 'upSellEnabled' | 'sectors'>;
  departurePoint?: Airport;
  arrivalPoint?: Airport;
  departureDate?: Date;
  distanceRestriction?: string;
  baggageIncluded?: boolean;
  roundTrip?: boolean;
  returnDate?: Date;
  cabin?: string;
}

const UpSellOptionsContainer = ({
  show,
  onClose,
  onSelect,
  onChange,
  selectedFlight,
  departurePoint,
  departureDate,
  arrivalPoint,
  distanceRestriction,
  baggageIncluded,
  roundTrip,
  returnDate,
  cabin,
}: Props) => {
  const { t } = useStoreTranslation();
  const { searchId, sessionToken, lowestLogicalFare } = useSelector((state: RootState) => ({
    searchId: state.flights.search?.searchId,
    sessionToken: state.user.accountResult?.sessionToken,
    lowestLogicalFare: state.flights.search?.lowestLogicalFare,
  }));
  const user = useSelector((state: RootState) => state.user?.accountResult);

  const [upSellId, setUpSellId] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [options, setOptions] = useState<UpSellOption[]>();

  useEffect(() => {
    const loadUpSell = async () => {
      if (!isLoaded && !isLoading) {
        if (
          searchId &&
          sessionToken &&
          user &&
          selectedFlight &&
          distanceRestriction &&
          departurePoint &&
          arrivalPoint &&
          departureDate
        ) {
          try {
            const payload: UpSellRequest = {
              searchId,
              sessionToken,
              userId: user.id,
              optionId: selectedFlight.optionId,
              DistanceRestriction: distanceRestriction,
              DepartureAirport: departurePoint.code,
              ArrivalAirport: arrivalPoint.code,
              DepartureDate: departureDate,
              lowestLogicalFare,
              SectorsCount: selectedFlight?.sectors?.length ?? 0,
            };
            const data = await getUpSellFares(payload);
            if (data.upsellOptions && data.upsellOptions.length > 0) {
              setOptions(data.upsellOptions);
              setUpSellId(data.upsellId);

              const amplitudeViewUpsell: ViewUpsellOptionsEvent = {
                TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
                TRAVEL_FROM: departurePoint?.code ?? '',
                TRAVEL_TO: arrivalPoint?.code ?? '',
                TRAVEL_DESTINATION_TYPE: '',
                TRAVEL_TRIP_TYPE: roundTrip ? 'true' : 'false',
                TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
                TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
                TRAVEL_DATE_TO: returnDate?.toString() ?? '',
                TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
                TRAVEL_PASSENGERS: '1',
                TRAVEL_ADULTS: '1',
                TRAVEL_CHILDREN: '0',
                TRAVEL_INFANTS: '0',
                TRAVEL_CLASS_OF_SERVICE: cabin ?? '',
                TRAVEL_ROOMS: '',
                TRAVEL_WITH_BAGGAGE: baggageIncluded ? 'true' : 'false',
                TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
                SEARCHID: searchId,
                TRAVEL_PRODUCT_NAME: selectedFlight?.provider ?? '',
                SOURCE: 'RESULTS',
              };
              amplitudelogEvent(amplitudeEvents.VIEW_UPSELL_OPTIONS, amplitudeViewUpsell, user);
            }
            setIsLoaded(true);
            setIsLoading(false);
          } catch (error) {
            onSelect(0, '');
            setIsLoaded(true);
            setIsLoading(false);
          }
        }
      }
    };
    if (show && !isLoaded && !isLoading && selectedFlight && selectedFlight.upSellEnabled) {
      setIsLoading(true);
      loadUpSell();
    }
  }, [
    isLoaded,
    isLoading,
    searchId,
    t,
    selectedFlight,
    user,
    departurePoint,
    arrivalPoint,
    departureDate,
    distanceRestriction,
    sessionToken,
    onSelect,
    show,
    lowestLogicalFare,
    roundTrip,
    returnDate,
    cabin,
    baggageIncluded,
  ]);

  return (
    <UpSellOptions
      loading={isLoading}
      options={options || []}
      onSelect={(optionId) => onSelect(optionId, upSellId || '')}
      onChange={onChange}
      onClose={onClose}
      show={true}
    />
  );
};
export default UpSellOptionsContainer;
